import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Checkbox
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

// ==================== INDEPENDENT AMOUNT INPUT ====================
const IndependentAmountInput = memo(({ value, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(value || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (!isInternalChange.current && value !== prevValueRef.current) {
      setLocalValue(value || "");
      prevValueRef.current = value;
    }
    isInternalChange.current = false;
  }, [value]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      prevValueRef.current = "";
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*\.?\d{0,2}$/.test(newValue) && newValue.length <= 10) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      prevValueRef.current = newValue;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    prevValueRef.current = "";
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <TextField
      fullWidth
      value={localValue}
      onChange={handleChange}
      onWheel={(e) => e.target.blur()}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }}
      placeholder="Enter amount limit"
      size="small"
      disabled={disabled}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <span style={{ color: '#666', fontWeight: 500 }}>₹</span>
          </InputAdornment>
        ),
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton onClick={handleClear} edge="end" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{
        mb: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
          '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
        }
      }}
    />
  );
});

// ==================== INDEPENDENT TRANSACTION INPUT ====================
const IndependentTransactionInput = memo(({ value, onValueChange, disabled }) => {
  const [localValue, setLocalValue] = useState(value || "");
  const isInternalChange = useRef(false);
  const timeoutRef = useRef(null);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (!isInternalChange.current && value !== prevValueRef.current) {
      setLocalValue(value || "");
      prevValueRef.current = value;
    }
    isInternalChange.current = false;
  }, [value]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      isInternalChange.current = true;
      prevValueRef.current = "";
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      onValueChange("");
      return;
    }
    
    if (/^\d*$/.test(newValue) && newValue.length <= 6) {
      setLocalValue(newValue);
      isInternalChange.current = true;
      prevValueRef.current = newValue;
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onValueChange(newValue);
      }, 300);
    }
  };

  const handleClear = () => {
    setLocalValue("");
    isInternalChange.current = true;
    prevValueRef.current = "";
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onValueChange("");
  };

  return (
    <TextField
      fullWidth
      value={localValue}
      onChange={handleChange}
      onWheel={(e) => e.target.blur()}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }}
      placeholder="Enter transaction count"
      size="small"
      disabled={disabled}
      InputProps={{
        endAdornment: localValue && (
          <InputAdornment position="end">
            <IconButton onClick={handleClear} edge="end" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
      sx={{
        mb: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
          '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
        }
      }}
    />
  );
});

// ==================== DURATION INPUT ====================
const DurationInput = memo(({ value, onChange, unit, disabled }) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef(null);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      setLocalValue(value);
      prevValueRef.current = value;
    }
  }, [value]);

  const handleChange = (e) => {
    let newValue = e.target.value;
    
    if (newValue === "") {
      setLocalValue("");
      return;
    }
    
    if (/^\d*$/.test(newValue)) {
      let numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && numValue > 0 && numValue <= 365) {
        setLocalValue(numValue);
        prevValueRef.current = numValue;
        
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          onChange(numValue);
        }, 300);
      } else if (newValue === "0") {
        setLocalValue(1);
        onChange(1);
      }
    }
  };

  const handleBlur = () => {
    let numValue = parseInt(localValue, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setLocalValue(1);
      onChange(1);
    } else if (numValue > 365) {
      setLocalValue(365);
      onChange(365);
    } else {
      onChange(numValue);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <TextField
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.target.blur()}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
          }
        }}
        size="small"
        disabled={disabled}
        inputProps={{
          min: 1,
          max: 365,
          style: { textAlign: 'center' }
        }}
        sx={{
          width: 100,
          '& input[type=number]': {
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            }
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78' } },
            '&.Mui-focused': { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#14AE78', borderWidth: 2 } }
          }
        }}
      />
      <Typography variant="caption" sx={{ color: "#666", minWidth: 40 }}>
        {unit}
      </Typography>
    </Box>
  );
});

// ==================== FREQUENCY BUTTONS ====================
const FrequencyButtons = memo(({ value, onChange, options, disabled }) => {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {options.map((option) => (
        <Button
          key={option.value}
          size="small"
          variant={value === option.value ? "contained" : "outlined"}
          onClick={() => onChange(option.value)}
          disabled={disabled}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: "12px",
            ...(value === option.value ? {
              background: "#14AE78",
              "&:hover": { background: "#0f8c65" }
            } : {})
          }}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  );
});

// ====================== AMOUNT SECTION ======================
const AmountSection = memo(({ 
  value,
  frequency,
  duration,
  onValueChange,
  onFrequencyChange,
  onDurationChange,
  loading,
  title,
  color,
  disabled
}) => {
  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: color || "#2e7d32", mb: 2 }}>
        {title || "Amount Limit"}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444" }}>
            Limit Value
          </Typography>
          <Typography variant="caption" sx={{ color: "#999" }}>
            {value?.length || 0}/10
          </Typography>
        </Box>
        <IndependentAmountInput
          value={value}
          onValueChange={onValueChange}
          disabled={loading || disabled}
        />
        <Typography variant="caption" sx={{ color: "#666" }}>
          Maximum amount (in ₹) per period
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Frequency
        </Typography>
        <FrequencyButtons
          value={frequency}
          onChange={onFrequencyChange}
          options={frequencyOptions}
          disabled={loading || disabled}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Duration
        </Typography>
        <DurationInput
          value={duration}
          onChange={onDurationChange}
          disabled={loading || disabled}
          unit={unit}
        />
      </Box>
    </Box>
  );
});

// ====================== TRANSACTION SECTION ======================
const TransactionSection = memo(({ 
  value,
  frequency,
  duration,
  onValueChange,
  onFrequencyChange,
  onDurationChange,
  loading,
  title,
  color,
  disabled
}) => {
  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTHS", label: "Month" },
  ];

  const getUnit = (freq) => {
    switch(freq) {
      case "HOURS": return "hrs";
      case "DAYS": return "days";
      case "MONTHS": return "months";
      default: return "days";
    }
  };

  const unit = getUnit(frequency);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: color || "#1565c0", mb: 2 }}>
        {title || "Transaction Limit"}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444" }}>
            Transaction Count
          </Typography>
          <Typography variant="caption" sx={{ color: "#999" }}>
            {value?.length || 0}/6
          </Typography>
        </Box>
        <IndependentTransactionInput
          value={value}
          onValueChange={onValueChange}
          disabled={loading || disabled}
        />
        <Typography variant="caption" sx={{ color: "#666" }}>
          Maximum number of transactions per period
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Frequency
        </Typography>
        <FrequencyButtons
          value={frequency}
          onChange={onFrequencyChange}
          options={frequencyOptions}
          disabled={loading || disabled}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
          Duration
        </Typography>
        <DurationInput
          value={duration}
          onChange={onDurationChange}
          disabled={loading || disabled}
          unit={unit}
        />
      </Box>
    </Box>
  );
});

// ==================== MAIN UPDATE COMPONENT ====================
const UpdateUsageLimitComponent = ({
  usageLimitRequired,
  handleUsageLimitChange,
  onLimitsChange,
  initialLimits = {},
  loading,
}) => {
  // Checkbox states
  const [enableBP, setEnableBP] = useState(false);
  const [enableTagPay, setEnableTagPay] = useState(false);
  
  // BP Limits states
  const [bpAmount, setBpAmount] = useState({ limitOn: "AMOUNT", frequency: "DAYS", duration: 1, value: "" });
  const [bpTransaction, setBpTransaction] = useState({ limitOn: "TRANSACTION", frequency: "DAYS", duration: 1, value: "" });
  
  // Tag Limits states
  const [tagAmount, setTagAmount] = useState({ limitOn: "AMOUNT", frequency: "DAYS", duration: 1, value: "" });
  const [tagTransaction, setTagTransaction] = useState({ limitOn: "TRANSACTION", frequency: "DAYS", duration: 1, value: "" });

  const isInitialized = useRef(false);

  // Initialize from existing data - runs only once
  useEffect(() => {
    if (!isInitialized.current && initialLimits && Object.keys(initialLimits).length > 0) {
      console.log("Initializing with data:", initialLimits);
      
      // Set account types checkboxes
      const accountTypes = initialLimits.accountTypes || [];
      setEnableBP(accountTypes.includes("WALLET"));
      setEnableTagPay(accountTypes.includes("WALLET_RFID"));
      
      // Set BP Limits
      if (initialLimits.bpLimits && initialLimits.bpLimits.length > 0) {
        const amount = initialLimits.bpLimits.find(l => l.limitOn === "AMOUNT");
        const transaction = initialLimits.bpLimits.find(l => l.limitOn === "TRANSACTION");
        if (amount) setBpAmount(amount);
        if (transaction) setBpTransaction(transaction);
      }
      
      // Set Tag Limits
      if (initialLimits.tagLimits && initialLimits.tagLimits.length > 0) {
        const amount = initialLimits.tagLimits.find(l => l.limitOn === "AMOUNT");
        const transaction = initialLimits.tagLimits.find(l => l.limitOn === "TRANSACTION");
        if (amount) setTagAmount(amount);
        if (transaction) setTagTransaction(transaction);
      }
      
      isInitialized.current = true;
    }
  }, [initialLimits]);

  // Send combined data to parent - debounced
  const timeoutRef = useRef(null);
  
  const sendCombinedData = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      const accountTypes = [];
      const bpLimits = [];
      const tagLimits = [];
      
      if (enableBP) {
        accountTypes.push("WALLET");
        if (bpAmount.value && parseFloat(bpAmount.value) > 0) {
          bpLimits.push(bpAmount);
        }
        if (bpTransaction.value && parseFloat(bpTransaction.value) > 0) {
          bpLimits.push(bpTransaction);
        }
      }
      
      if (enableTagPay) {
        accountTypes.push("WALLET_RFID");
        if (tagAmount.value && parseFloat(tagAmount.value) > 0) {
          tagLimits.push(tagAmount);
        }
        if (tagTransaction.value && parseFloat(tagTransaction.value) > 0) {
          tagLimits.push(tagTransaction);
        }
      }
      
      const usageLimitData = {
        usageLimitRequired: usageLimitRequired === "Yes",
        accountTypes: accountTypes,
      };
      
      if (bpLimits.length > 0) usageLimitData.bpLimits = bpLimits;
      if (tagLimits.length > 0) usageLimitData.tagLimits = tagLimits;
      
      if (onLimitsChange && (enableBP || enableTagPay)) {
        onLimitsChange(usageLimitData);
      } else if (!enableBP && !enableTagPay && usageLimitRequired === "Yes") {
        onLimitsChange({ usageLimitRequired: true, accountTypes: [], bpLimits: [], tagLimits: [] });
      }
    }, 300);
  }, [enableBP, enableTagPay, bpAmount, bpTransaction, tagAmount, tagTransaction, usageLimitRequired, onLimitsChange]);

  // Send data when any limit changes
  useEffect(() => {
    if (usageLimitRequired === "Yes" && isInitialized.current) {
      sendCombinedData();
    }
  }, [enableBP, enableTagPay, bpAmount, bpTransaction, tagAmount, tagTransaction, sendCombinedData, usageLimitRequired]);

  const handleBPChange = (e) => {
    setEnableBP(e.target.checked);
    if (!e.target.checked) {
      setBpAmount({ limitOn: "AMOUNT", frequency: "DAYS", duration: 1, value: "" });
      setBpTransaction({ limitOn: "TRANSACTION", frequency: "DAYS", duration: 1, value: "" });
    }
  };

  const handleTagPayChange = (e) => {
    setEnableTagPay(e.target.checked);
    if (!e.target.checked) {
      setTagAmount({ limitOn: "AMOUNT", frequency: "DAYS", duration: 1, value: "" });
      setTagTransaction({ limitOn: "TRANSACTION", frequency: "DAYS", duration: 1, value: "" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={32} />
        <Typography sx={{ ml: 2, color: '#666' }}>Loading usage limit data...</Typography>
      </Box>
    );
  }

  const isEditDisabled = false;

  return (
    <Box sx={{ maxWidth: 800, margin: "auto" }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
        Usage Limit
      </Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
        Define spending thresholds and transaction volume controls for this corporate account.
      </Typography>

      <Box sx={{ background: "#f8f9fa", borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
          Do you want to set a usage limit?
        </Typography>

        <RadioGroup
          value={usageLimitRequired}
          onChange={(e) => handleUsageLimitChange(e.target.value)}
          row
          sx={{ gap: 3 }}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Yes</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  Set a usage limit for corporate
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "Yes" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
          <FormControlLabel
            value="No"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>No</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  No usage limit required
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "No" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
        </RadioGroup>
      </Box>

      {usageLimitRequired === "Yes" && (
        <Box sx={{ background: "white", borderRadius: 2, p: 3, border: "1px solid #e0e0e0", mt: 2 }}>
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
            Select Account Type
          </Typography>
          
          <Box sx={{ display: "flex", gap: 4, mb: 4 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={enableBP}
                  onChange={handleBPChange}
                  sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }}
                />
              }
              label="Business Payment (BP)"
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={enableTagPay}
                  onChange={handleTagPayChange}
                  sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }}
                />
              }
              label="TagPay"
            />
          </Box>

          {/* BP Limits - Only show if enabled */}
          {enableBP && (
            <>
              <AmountSection
                value={bpAmount.value?.toString() || ""}
                frequency={bpAmount.frequency}
                duration={bpAmount.duration}
                onValueChange={(newValue) => setBpAmount(prev => ({ ...prev, value: newValue }))}
                onFrequencyChange={(newFreq) => setBpAmount(prev => ({ ...prev, frequency: newFreq, duration: 1 }))}
                onDurationChange={(newDuration) => setBpAmount(prev => ({ ...prev, duration: newDuration }))}
                loading={loading}
                title="BP Amount Limit"
                color="#2e7d32"
                disabled={false}
              />
              <TransactionSection
                value={bpTransaction.value?.toString() || ""}
                frequency={bpTransaction.frequency}
                duration={bpTransaction.duration}
                onValueChange={(newValue) => setBpTransaction(prev => ({ ...prev, value: newValue }))}
                onFrequencyChange={(newFreq) => setBpTransaction(prev => ({ ...prev, frequency: newFreq, duration: 1 }))}
                onDurationChange={(newDuration) => setBpTransaction(prev => ({ ...prev, duration: newDuration }))}
                loading={loading}
                title="BP Transaction Limit"
                color="#1565c0"
                disabled={false}
              />
            </>
          )}

          {/* TagPay Limits - Only show if enabled */}
          {enableTagPay && (
            <>
              <AmountSection
                value={tagAmount.value?.toString() || ""}
                frequency={tagAmount.frequency}
                duration={tagAmount.duration}
                onValueChange={(newValue) => setTagAmount(prev => ({ ...prev, value: newValue }))}
                onFrequencyChange={(newFreq) => setTagAmount(prev => ({ ...prev, frequency: newFreq, duration: 1 }))}
                onDurationChange={(newDuration) => setTagAmount(prev => ({ ...prev, duration: newDuration }))}
                loading={loading}
                title="TagPay Amount Limit"
                color="#2e7d32"
                disabled={false}
              />
              <TransactionSection
                value={tagTransaction.value?.toString() || ""}
                frequency={tagTransaction.frequency}
                duration={tagTransaction.duration}
                onValueChange={(newValue) => setTagTransaction(prev => ({ ...prev, value: newValue }))}
                onFrequencyChange={(newFreq) => setTagTransaction(prev => ({ ...prev, frequency: newFreq, duration: 1 }))}
                onDurationChange={(newDuration) => setTagTransaction(prev => ({ ...prev, duration: newDuration }))}
                loading={loading}
                title="TagPay Transaction Limit"
                color="#1565c0"
                disabled={false}
              />
            </>
          )}

          {!enableBP && !enableTagPay && (
            <Typography variant="body2" sx={{ color: "#999", textAlign: "center", py: 4 }}>
              Please select at least one account type
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default UpdateUsageLimitComponent;